import classNames from 'classnames';
import {
  memo,
  useMemo,
  useRef,
  type FunctionComponent,
  type ReactElement,
} from 'react';

import { useMemberId } from '@trello/authentication';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import type { ReactionResponse } from '@trello/realtime-updater';
import type { CardBackTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Helpers as AttachmentHelpers } from 'app/scripts/views/attachment/Helpers';
import type {
  ActionEntityType,
  CommentEntity,
} from 'app/src/components/ActionEntities';
import { ActionEntity } from 'app/src/components/ActionEntities/ActionEntity';
import { useCanCommentOnBoard } from 'app/src/components/BoardPermissionsContext';
import { PersistentScrollIntoView } from 'app/src/components/CardComposer/usePersistentScrollIntoView';
import { MemberAvatarWithProfileCard } from 'app/src/components/ProfileCard';
import { ActionContextProvider } from './ActionContext';
import { ActionDisplay } from './ActionDisplay';
import { CommentDisplay } from './CommentDisplay';
import { useCommentMemberFragment } from './CommentMemberFragment.generated';
import { EntityWithAttachmentViewer } from './EntityWithAttachmentViewer';
import { getContextualizedTranslationKey } from './getContextualizedTranslationKey';
import { replyToAttachment } from './replyToComment';
import { useActionContext } from './useActionContext';

import * as styles from './Action.module.less';

interface ActionWrappedProps {
  id: string;
  memberId: string;
  date: string;
  translationKey: string;
  entities: string;
  cardShortUrl?: string;
  isHighlighted?: boolean;
  appCreator?: {
    id?: string | null;
    name?: string | null;
  } | null;
  reactions: ReactionResponse[];
  showReactions?: boolean;
  isEdited?: boolean;
  cardId?: string;
  boardName?: string;
  firstFocusableRef?: React.Ref<HTMLElement>;
}

interface ActionProps extends ActionWrappedProps {
  location: 'board' | 'card' | 'member';
}

const translationKeysToOverride: Record<string, string> = {
  action_marked_the_due_date_complete: 'action_marked_the_done_state_complete',
  action_marked_the_due_date_incomplete:
    'action_marked_the_done_state_incomplete',
};

export const ActionWrapped: FunctionComponent<ActionWrappedProps> = ({
  id,
  memberId,
  date,
  translationKey,
  entities,
  cardShortUrl,
  isHighlighted,
  reactions,
  showReactions = true,
  isEdited,
  appCreator,
  cardId,
  boardName,
  firstFocusableRef,
}) => {
  const wrapperRef = useRef<HTMLLIElement>(null);
  const actionCardId = cardId || String(JSON.parse(entities).card?.id || '');
  const shortUrlFromEntities = JSON.parse(entities).card?.shortLink || '';
  // sort of complex, but if if don't have a card ID, then we don't need a short URL and can use undefined
  // if we have a card ID, then we use the provided short URL if provided or generate one if not provided
  const actionCardShortUrl = actionCardId
    ? cardShortUrl || `/c/${shortUrlFromEntities}`
    : undefined;
  const { location } = useActionContext();

  const { data: commenterNameData } = useCommentMemberFragment({
    from: { id: memberId },
    optimistic: true,
  });

  const currentMemberId = useMemberId();
  const canComment = useCanCommentOnBoard();
  const isRepliableAddAttachmentAction =
    canComment && translationKey === 'action_add_attachment_to_card';

  const contextualTranslationKey =
    translationKey in translationKeysToOverride
      ? getContextualizedTranslationKey(
          translationKeysToOverride[translationKey],
          location,
        )
      : getContextualizedTranslationKey(translationKey, location);

  const parsedEntities = JSON.parse(entities);
  const onReplyRef = useRef(() => {});
  const { memoizedSubstitutions, memoizedComment } = useMemo(() => {
    const comment: CommentEntity = parsedEntities.comment;
    const substitutions: {
      [key: string]: ReactElement | string;
    } = {};

    Object.entries<ActionEntityType>(parsedEntities).forEach(([key, value]) => {
      // We remove comments from the substitutions object since it is a special case.
      // It's extracted above to be displayed in the comment box separate from the translation.
      if (value.type === 'comment') {
        substitutions[key] = '';
        // The default ActionEntity bolds these action types. On the card back specifically, we just want plain text.
      } else if (value.type === 'checkItem' || value.type === 'checklist') {
        substitutions[key] = value.text;
      } else if (value.type === 'attachment') {
        if (isRepliableAddAttachmentAction && value.url) {
          const attachmentUrl = value.url;
          onReplyRef.current = () => {
            replyToAttachment({
              attachmentUrl,
              attachmentText: value.text,
              username:
                memberId !== currentMemberId
                  ? dangerouslyConvertPrivacyString(commenterNameData?.username)
                  : undefined,
            });
          };
        }
        if (AttachmentHelpers.isViewerable(value.url) && value.id) {
          substitutions[key] = (
            <EntityWithAttachmentViewer
              attachmentId={value.id}
              actionId={id}
              children={value.text}
              cardId={actionCardId}
            />
          );
        } else {
          substitutions[key] = <ActionEntity entity={value} />;
        }
      } else if (value.type === 'attachmentPreview') {
        substitutions[key] = (
          <EntityWithAttachmentViewer
            attachmentId={value.id}
            actionId={id}
            children={<ActionEntity entity={value} />}
            cardId={actionCardId}
          />
        );
      } else {
        substitutions[key] = <ActionEntity entity={value} />;
      }
    });
    return { memoizedSubstitutions: substitutions, memoizedComment: comment };
  }, [
    actionCardId,
    commenterNameData?.username,
    currentMemberId,
    id,
    isRepliableAddAttachmentAction,
    memberId,
    parsedEntities,
  ]);

  return (
    <li
      className={classNames({
        [styles.wrapper]: true,
        [styles.highlightedWrapper]: isHighlighted,
        [styles.boardAction]: ['board', 'member'].includes(location),
      })}
      ref={wrapperRef}
      data-testid={getTestId<CardBackTestIds>('card-back-action')}
    >
      {/* Wrapper div is needed to pin the avatar to the top of the grid row */}
      <div>
        <MemberAvatarWithProfileCard
          memberId={memberId}
          isRemoveMemberOptionEnabled={false}
          size={32}
          cardId={actionCardId}
          tabIndex={0}
          avatarRef={firstFocusableRef}
        />
      </div>
      {translationKey === 'action_copy_comment_from_card' ||
      translationKey === 'action_comment_on_card' ? (
        <CommentDisplay
          cardShortUrl={actionCardShortUrl}
          commentId={id}
          memberId={memberId}
          messageId={`actions.${contextualTranslationKey}`}
          date={date}
          substitutions={memoizedSubstitutions}
          comment={memoizedComment}
          isHighlighted={isHighlighted}
          reactions={reactions}
          showReactions={showReactions}
          isEdited={isEdited}
          appCreator={appCreator}
          cardId={actionCardId}
          boardName={boardName}
        />
      ) : (
        <ActionDisplay
          cardShortUrl={actionCardShortUrl}
          actionId={id}
          // The @card suffix indicates that the action is taking place on the existing card, which has a different translation.
          // e.g. "completed task on this card" will render instead of "completed task on <a>card xyz</a>".
          messageId={`actions.${contextualTranslationKey}`}
          date={date}
          substitutions={memoizedSubstitutions}
          isHighlighted={isHighlighted}
          appCreator={appCreator}
          onReply={onReplyRef.current}
          isRepliableAddAttachmentAction={isRepliableAddAttachmentAction}
          boardName={boardName}
        />
      )}
      {isHighlighted && <PersistentScrollIntoView scrollRef={wrapperRef} />}
    </li>
  );
};

// This is wrapped in a memo to prevent unnecessary rerenders when the isHighlighted prop is re-calculated
export const Action: FunctionComponent<ActionProps> = memo((props) => {
  const { location, ...rest } = props;
  return (
    <ActionContextProvider location={location}>
      <ActionWrapped {...rest} />
    </ActionContextProvider>
  );
});
